import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import DashboardLayout from '../DashboardLayout'
import CustomTabs from '../../CustomTabs'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import FullScreenLoader from '../../FullScreenLoader'
import { useProjectPermission } from '../../../../context/ProjectContext'
import { trackPromise } from 'react-promise-tracker'
import api from '../../../../api'
import { getLocalStorage, getPermissions, hasPermission, homeScreenRedirection, isSuccess, showError } from '../../../../utils/helper'
import { auditTabs, inspectionTabs, naiFlowDashboardsTabs, naiFlowTabs, naiFlowViewTabs, projectUserManagementTabs, statisticCaptureTabs } from '../../../../routes/allPaths'
import NaiHubDashboard from '../../../../pages/Dashboard/NaiHubDashboard'

// Enhanced tab configuration interface
interface TabConfig {
  label: string
  slug: string
  path?: string // Custom path override
  disabled?: boolean // Allow disabling specific tabs
  badge?: string | number // Optional badge/count display
  icon?: React.ReactNode // Optional icon
  hidden?: boolean // Allow hiding tabs conditionally
  page: string
}

interface Props {
  breadcrumbData?: any
  children: React.ReactNode
  page: string
  parentPage?: string
  baseUrl?: string // Base URL pattern for generating tab paths
  onTabChange?: (activeTab: TabConfig, index: number) => void // Callback for tab changes
  loading?: boolean // External loading state
  fallbackTabIndex?: number // Fallback tab index if no match found
  preserveQueryParams?: boolean // Whether to preserve query parameters when navigating
  className?: string // Additional CSS classes
  clickableBreadcrumbIndices?: number[]
}

const TabsLayout: React.FC<Props> = ({
  breadcrumbData,
  children,
  page,
  baseUrl,
  onTabChange,
  loading = false,
  fallbackTabIndex = 0,
  preserveQueryParams = false,
  className = '',
  parentPage,
  clickableBreadcrumbIndices,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams() as any
  const { projectPermission, setProjectPermission } = useProjectPermission()
  const { name, id, subId, subName, resourceName, resourceId, subsidiaryId } = params as any
  const [isInitialized, setIsInitialized] = useState(false)

  // Use refs only for navigation to avoid stale closures
  const latestLocation = useRef(location)

  // Update ref when location changes
  useEffect(() => {
    latestLocation.current = location
  }, [location])

  // Fetch project permissions - simplified dependency array
  // Skip permission fetch for nai-flow-view pages as they don't require permission checks
  useEffect(() => {
    if (!projectPermission && id) {
      if (page === 'nai-flow-view' || parentPage === 'nai-flow-view') {
        if (subsidiaryId) {
          trackPromise(
            api
              .getLinkedProjectPermissions(id, subsidiaryId)
              .then((response: any) => {
                if (isSuccess(response)) {
                  const d = response?.data?.data
                  const arr: any = [...(d?.modules || [])]
                  arr.is_admin_user = d?.is_admin_user
                  arr.is_project_admin = d?.is_project_admin
                  setProjectPermission(arr)
                }
              })
              .catch((error: any) => {
                showError(error)
              }),
          )
        }
      } else {
        trackPromise(
          api
            .getProjectPermissionsByProjectID(id)
            .then((response: any) => {
              if (isSuccess(response)) {
                const d = response?.data?.data
                const arr: any = [...(d?.modules || [])]
                arr.is_admin_user = d?.is_admin_user
                arr.is_project_admin = d?.is_project_admin
                setProjectPermission(arr)
              }
            })
            .catch((error: any) => {
              showError(error)
            }),
        )
      }
    }
  }, [id, subsidiaryId, projectPermission, setProjectPermission, page, parentPage])

  // Memoize path replacement function
  const replacePath = useCallback(
    (path: string) => {
      return path
        ?.replace(':name', name || '')
        ?.replace(':id', id || '')
        ?.replace(':subsidiaryId', subsidiaryId || '')
        ?.replace(':subName', subName || '')
        ?.replace(':subId', subId || '')
        ?.replace(':resourceName', resourceName || '')
        ?.replace(':resourceId', resourceId || '')
    },
    [name, id, subsidiaryId, subName, subId, resourceName, resourceId],
  )

  // Memoize param replacement for tabs
  const handleParamReplace = useCallback(
    (tabs: TabConfig[]) => {
      return tabs?.map((tab: TabConfig) => ({
        ...tab,
        path: replacePath(tab?.path || ''),
      }))
    },
    [replacePath],
  )

  // Memoize tabs calculation
  const tabs = useMemo(() => {
    const getFirstAllowedSlug = (tabs: any, pageName: string) => {
      return [...tabs]?.filter((slug: TabConfig) => slug?.page === pageName).find((slug: TabConfig) => hasPermission(projectPermission, slug?.slug, 1))
    }

    const naiHubTabs = getPermissions()
      ?.filter((ls: any) => ls?.is_nai_hub)
      ?.map((tab: any) => {
        return {
          label: tab?.module_name,
          component: NaiHubDashboard,
          path: `/dashboard/nai-hub/${tab?.module_name}/${tab?.slug}`,
          slug: tab?.slug,
          page: 'nai-hub-dashboard',
        }
      })

    let allTabs = [
      ...handleParamReplace(projectUserManagementTabs),
      ...handleParamReplace([...naiFlowTabs]),
      ...handleParamReplace([...naiFlowViewTabs]),
      ...handleParamReplace(inspectionTabs),
      ...handleParamReplace(auditTabs),
      ...handleParamReplace(statisticCaptureTabs),
      ...handleParamReplace(naiFlowDashboardsTabs),
      {
        label: 'Details',
        component: children,
        path: `/project-management/${name}/${id}/details`,
        slug: 'details-page',
        page: 'project-page',
      },
      {
        label: 'Rules And Escalation',
        component: children,
        path: `/project-management/${name}/${id}/rules-and-escalation`,
        slug: 'rules-escalation',
        hidden: !hasPermission(projectPermission, 'rules-escalation', 1),
        page: 'project-page',
      },
      {
        label: 'User Management',
        component: children,
        path: `/project-management/${name}/${id}/user-management/${hasPermission(projectPermission, 'user', 1) ? 'users' : 'roles'}`,
        slug: 'user-management',
        hidden: !hasPermission(projectPermission, 'user', 1) && !hasPermission(projectPermission, 'role', 1),
        page: 'project-page',
      },
      {
        label: 'nAI Flow',
        path: replacePath(getFirstAllowedSlug(naiFlowTabs, 'nai-flow')?.path || ''),
        slug: 'nai-flow',
        hidden: !getFirstAllowedSlug(naiFlowTabs, 'nai-flow'),
        page: 'project-page',
      },
      {
        label: 'nAI Flow',
        path: replacePath(getFirstAllowedSlug(naiFlowViewTabs, 'nai-flow-view')?.path || ''),
        slug: 'nai-flow-view',
        hidden: !getFirstAllowedSlug(naiFlowViewTabs, 'nai-flow-view'),
        page: 'project-page',
      },
      {
        label: 'nAI Hub',
        path: `/project-management/${name}/${id}/nai-hub`,
        slug: 'nai-hub',
        hidden: !hasPermission(projectPermission, 'nai-hub', 1),
        page: 'project-page',
      },
      {
        label: 'Details',
        component: children,
        path: `/project-management/${name}/${id}/nai-hub/edit/${subName}/${subId}/${resourceName}/${resourceId}/details`,
        slug: 'nai-hub-details',
        page: 'nai-hub-page',
      },
      // Moved naiHub dashboard to Global level, till got final confirmation commented at project level
      // {
      //   label: 'Dashboard',
      //   component: children,
      //   path: `/project-management/${name}/${id}/nai-hub/edit/${subName}/${subId}/${resourceName}/${resourceId}/nai-hub-dashboard`,
      //   slug: 'nai-hub-dashboard',
      //   page: 'nai-hub-page',
      // },
      ...naiHubTabs,
    ]

    const projectManagementPages = ['project-user-management', 'nai-flow', 'nai-flow-view', 'inspections', 'statistics-capture', 'project-page', 'nai-hub-page']
    const dashboardPages = ['nai-flow-dashboard']
    // 'org-dashboard'
    const byPassSlugs = ['nai-flow', 'statistics-capture-schedule', 'statistics-capture-report', 'details-page', 'schedule', 'non-conformance', 'org-dashboard']

    // For nai-flow-view pages, show all tabs without permission checks
    if (page === 'nai-flow-view' || parentPage === 'nai-flow-view') {
      return allTabs?.filter(
        (tab: TabConfig) => tab.page === page && !tab.hidden && (hasPermission(projectPermission, tab?.slug, 1) || byPassSlugs?.includes(tab?.slug)),
      )
    } else if (projectManagementPages?.includes(page)) {
      return allTabs?.filter((tab: TabConfig) => tab.page === page && !tab.hidden && (hasPermission(projectPermission, tab?.slug, 1) || byPassSlugs?.includes(tab?.slug)))
    } else if (dashboardPages?.includes(page)) {
      return allTabs?.filter(
        (tab: TabConfig) => tab.page === page && !tab.hidden && (hasPermission(JSON.parse(`${getLocalStorage('permissions')}`), tab?.slug, 1) || byPassSlugs?.includes(tab?.slug)),
      )
    } else {
      return allTabs?.filter((tab: TabConfig) => tab.page === page && !tab.hidden)
    }
  }, [page, handleParamReplace, replacePath, children, projectPermission, name, id, subsidiaryId, subId, subName, resourceName, resourceId]) as any

  // Generate URL for a given tab - simplified and memoized
  const generateURL = useCallback(
    (tab: TabConfig): string => {
      if (tab.path) {
        return tab.path
      }

      if (baseUrl) {
        return baseUrl
          .replace('{slug}', tab.slug)
          .replace('{name}', name || '')
          .replace('{id}', id || '')
          .replace('{subId}', subId || '')
          .replace('{subName}', subName || '')
      }

      // Default URL pattern
      return `/project-management/${name}/${id}/task-manager/${tab.slug}`
    },
    [baseUrl, name, id, subsidiaryId, subId, subName],
  )

  // Match pathname to tab - supports exact match and sub-routes (e.g. /reports/123 matches Report tab)
  const findActiveTabIndex = useCallback(
    (pathname: string, tabList: TabConfig[]) => {
      const currentPath = pathname?.toLowerCase() || ''
      let activeIndex = tabList.findIndex((tab: TabConfig) => tab?.path?.toLowerCase() === currentPath)
      if (activeIndex >= 0) return activeIndex
      const tabPathMatches = tabList
        .map((tab, idx) => ({ idx, path: tab?.path?.toLowerCase() }))
        .filter(({ path }) => path && currentPath.startsWith(path + '/'))
      if (tabPathMatches.length > 0) {
        const bestMatch = tabPathMatches.reduce((a, b) => (a.path!.length >= b.path!.length ? a : b))
        return bestMatch.idx
      }
      return -1
    },
    [],
  )

  // Helper function to compute active tab index, factoring in disabled tabs and fallback logic
  const getComputedTabIndex = useCallback(
    (pathname: string, tabList: TabConfig[], fallbackIndex: number) => {
      if (tabList.length === 0) return 0

      let activeIndex = findActiveTabIndex(pathname, tabList)

      // If no match found, use fallback
      if (activeIndex === -1) {
        activeIndex = Math.min(fallbackIndex, tabList.length - 1)
      }

      // Ensure the tab at this index is not disabled
      while (activeIndex < tabList.length && tabList[activeIndex]?.disabled) {
        activeIndex++
      }

      // If all tabs after fallback are disabled, look backwards
      if (activeIndex >= tabList.length) {
        activeIndex = Math.max(0, fallbackIndex - 1)
        while (activeIndex >= 0 && tabList[activeIndex]?.disabled) {
          activeIndex--
        }
      }

      return activeIndex >= 0 ? activeIndex : 0
    },
    [findActiveTabIndex]
  )

  // Find active tab index - simplified effect
  useEffect(() => {
    if (tabs.length === 0) {
      setIsInitialized(true)
      return
    }
    const currentPathname = location.pathname?.toLowerCase()
    const finalIndex = getComputedTabIndex(currentPathname, tabs, fallbackTabIndex)
    setIsInitialized(true)

    // Trigger callback if provided
    if (onTabChange && finalIndex >= 0 && tabs[finalIndex]) {
      onTabChange(tabs[finalIndex], finalIndex)
    }
  }, [tabs, location.pathname, fallbackTabIndex, onTabChange, projectPermission, getComputedTabIndex])

  // Handle tab navigation - simplified callback
  const handleTabClick = useCallback(
    (url: string) => {
      const currentLocation = latestLocation.current
      const finalUrl = preserveQueryParams && currentLocation.search ? `${url}${currentLocation.search}` : url
      navigate(finalUrl)
    },
    [navigate, preserveQueryParams],
  )

  // Transform tabs for CustomTabs component - better memoization
  const transformedTabs = useMemo(
    () =>
      tabs.map((tab: TabConfig) => ({
        label: tab.label,
        component: children,
        value: generateURL(tab),
        disabled: tab.disabled,
        badge: tab.badge,
        icon: tab.icon,
      })),
    [tabs, children, generateURL],
  )

  // Show loading state
  if (!isInitialized || loading) {
    return (
      <DashboardLayout breadcrumbData={breadcrumbData} clickableBreadcrumbIndices={clickableBreadcrumbIndices}>
        <div className={`grid grid-cols-12 h-full ${className}`}>
          <div className="col-span-12">
            <FullScreenLoader loading={true} />
          </div>
        </div>
      </DashboardLayout>
    )
  }

  // Show error state if no visible tabs
  if (tabs.length === 0) {
    return (
      <DashboardLayout breadcrumbData={breadcrumbData} clickableBreadcrumbIndices={clickableBreadcrumbIndices}>
        <div className={`grid grid-cols-12 h-full ${className}`}>
          <div className="col-span-12 flex items-center justify-center">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tabs available</h3>
              {/* <p className="text-gray-500">Please check your tab configuration.</p> */}
            </div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  const getDefaultIndex = () => {
    if (tabs.length === 0) {
      setIsInitialized(true)
      return 0
    }

    const currentPathname = location.pathname?.toLowerCase()
    return getComputedTabIndex(currentPathname, tabs, fallbackTabIndex)
  }

  return (
    <DashboardLayout
      breadcrumbData={breadcrumbData}
      page={page}
      parentPage={parentPage}
      disableBreadcrumbLinks={page === 'nai-flow-view' || parentPage === 'nai-flow-view'}
      clickableBreadcrumbIndices={clickableBreadcrumbIndices}
    >
      <div className={`grid grid-cols-12 h-full ${className}`}>
        {projectPermission || !location.pathname?.toLowerCase()?.includes('project-management') ? (
          <div className="col-span-12">
            <CustomTabs handleClick={handleTabClick} tabs={transformedTabs} defaultTabIndex={getDefaultIndex() || 0} />
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  )
}

export default TabsLayout

// TODO: @vijay needed below code as a backup

// import React, { useEffect, useState, useMemo, useCallback } from 'react'
// import DashboardLayout from '../DashboardLayout'
// import CustomTabs from '../../CustomTabs'
// import { useLocation, useNavigate, useParams } from 'react-router-dom'
// import FullScreenLoader from '../../FullScreenLoader'
// import { useProjectPermission } from '../../../../context/ProjectContext'
// import { trackPromise } from 'react-promise-tracker'
// import api from '../../../../api'
// import { hasPermission, isSuccess, showError } from '../../../../utils/helper'
// import { inspectionTabs, naiFlowDashboardsTabs, naiFlowTabs, projectUserManagementTabs, statisticCaptureTabs } from '../../../../routes/allPaths'

// // Enhanced tab configuration interface
// interface TabConfig {
//   label: string
//   slug: string
//   path?: string // Custom path override
//   disabled?: boolean // Allow disabling specific tabs
//   badge?: string | number // Optional badge/count display
//   icon?: React.ReactNode // Optional icon
//   hidden?: boolean // Allow hiding tabs conditionally
//   page: string
// }

// interface Props {
//   breadcrumbData?: any
//   children: React.ReactNode
//   page: string
//   parentPage?: string
//   baseUrl?: string // Base URL pattern for generating tab paths
//   onTabChange?: (activeTab: TabConfig, index: number) => void // Callback for tab changes
//   loading?: boolean // External loading state
//   fallbackTabIndex?: number // Fallback tab index if no match found
//   preserveQueryParams?: boolean // Whether to preserve query parameters when navigating
//   className?: string // Additional CSS classes
// }

// const TabsLayout: React.FC<Props> = ({
//   breadcrumbData,
//   children,
//   page,
//   baseUrl,
//   onTabChange,
//   loading = false,
//   fallbackTabIndex = 0,
//   preserveQueryParams = false,
//   className = '',
//   parentPage,
// }) => {
//   const navigate = useNavigate()
//   const location = useLocation()
//   const params = useParams() as any
//   const { projectPermission, setProjectPermission } = useProjectPermission()
//   const { name, id, subId, subName, resourceName, resourceId } = params as any
//   const [defaultTabIndex, setDefaultTabIndex] = useState<number | null>(null)
//   const [isInitialized, setIsInitialized] = useState(false)

//   useEffect(() => {
//     if (!projectPermission && id) {
//       trackPromise(
//         api
//           .getProjectPermissionsByProjectID(id)
//           .then((response: any) => {
//             if (isSuccess(response)) {
//               setProjectPermission(response?.data?.data)
//             }
//           })
//           .catch((error: any) => {
//             showError(error)
//           }),
//       )
//     }
//   }, [projectPermission, name, id])

//   const handleParamReplace = useCallback(
//     (tabs: any) => {
//       return tabs?.map((tab: TabConfig) => {
//         tab.path = replacePath(tab?.path || '')
//         return tab
//       })
//     },
//     [name, id, subName, subId, resourceName, resourceId, location?.pathname, projectPermission],
//   )

//   const replacePath = useCallback(
//     (path: string) => {
//       console.log('vijaty', name)

//       return path
//         ?.replace(':name', name)
//         ?.replace(':id', id)
//         ?.replace(':subName', subName)
//         ?.replace(':subId', subId)
//         ?.replace(':resourceName', resourceName)
//         ?.replace(':resourceId', resourceId)
//     },
//     [name, id, subName, subId, resourceName, resourceId, location?.pathname, projectPermission],
//   )

//   const tabs = useMemo(() => {
//     const getFirstAllowedSlug = () => {
//       return [...naiFlowTabs]?.filter((slug: TabConfig) => slug?.page === 'nai-flow').find((slug: TabConfig) => hasPermission(projectPermission, slug?.slug, 1))
//     }

//     console.log('Final:', ...handleParamReplace([...naiFlowTabs]), ...handleParamReplace(projectUserManagementTabs))
//     const allTabs = [
//       ...handleParamReplace(projectUserManagementTabs),
//       ...handleParamReplace([...naiFlowTabs]),
//       ...handleParamReplace(inspectionTabs),
//       ...handleParamReplace(statisticCaptureTabs),
//       ...handleParamReplace(naiFlowDashboardsTabs),
//       {
//         label: 'Details',
//         component: children,
//         path: `/project-management/${name}/${id}/details`,
//         slug: 'details-page',
//         page: 'project-page',
//       },
//       {
//         label: 'User Management',
//         component: children,
//         path: `/project-management/${name}/${id}/user-management/${hasPermission(projectPermission, 'user', 1) ? 'users' : 'roles'}`,
//         slug: 'user-management',
//         hidden: !hasPermission(projectPermission, 'user', 1) && !hasPermission(projectPermission, 'role', 1),
//         page: 'project-page',
//       },
//       {
//         label: 'nAI Flow',
//         path: replacePath(getFirstAllowedSlug()?.path),
//         slug: 'nai-flow',
//         hidden: !getFirstAllowedSlug(),
//         page: 'project-page',
//       },

//       {
//         label: 'nAI Hub',
//         path: `/project-management/${name}/${id}/nai-hub`,
//         slug: 'nai-hub',
//         hidden: !hasPermission(projectPermission, 'nai-hub', 1),
//         page: 'project-page',
//       },
//       {
//         label: 'Details',
//         component: children,
//         path: `/project-management/${name}/${id}/nai-hub/edit/${subName}/${subId}/${resourceName}/${resourceId}/details`,
//         slug: 'nai-hub-details',
//         page: 'nai-hub-page',
//       },
//       {
//         label: 'Dashboard',
//         component: children,
//         path: `/project-management/${name}/${id}/nai-hub/edit/${subName}/${subId}/${resourceName}/${resourceId}/nai-hub-dashboard`,
//         slug: 'nai-hub-dashboard',
//         page: 'nai-hub-page',
//       },
//     ]

//     const projectManagementPages = ['project-user-management', 'nai-flow', 'inspections', 'statistics-capture', 'nai-flow-dashboard', 'project-page', 'nai-hub-page']
//     const byPassSlugs = [
//       'nai-flow',
//       'statistics-capture-schedule',
//       'statistics-capture-report',
//       'details-page',
//       'observation-dashboard',
//       'incident-dashboard',
//       'inspection-dashboard',
//       'training-dashboard',
//       'statistics-capture-dashboard',
//     ]
//     if (projectManagementPages?.includes(page)) {
//       return allTabs.filter((tab: TabConfig) => tab.page === page && !tab.hidden && (hasPermission(projectPermission, tab?.slug, 1) || byPassSlugs?.includes(tab?.slug)))
//     } else {
//       return allTabs.filter((tab: TabConfig) => tab.page === page && !tab.hidden)
//     }
//   }, [projectPermission, page, name, id, location?.pathname]) as any

//   // Generate URL for a given tab
//   const generateURL = useCallback(
//     (tab: TabConfig): string => {
//       if (tab.path) {
//         return tab.path
//       }

//       if (baseUrl) {
//         // Replace placeholders in baseUrl
//         return baseUrl
//           .replace('{slug}', tab.slug)
//           .replace('{name}', name || '')
//           .replace('{id}', id || '')
//           .replace('{subId}', subId || '')
//           .replace('{subName}', subName || '')
//       }

//       // Default URL pattern
//       return `/project-management/${name}/${id}/task-manager/${tab.slug}`
//     },
//     [baseUrl, name, id, subId, subName, projectPermission, location?.pathname],
//   )

//   // Find active tab index
//   useEffect(() => {
//     if (tabs.length === 0) return
//     let activeIndex = tabs.findIndex((tab: TabConfig) => tab?.path?.toLowerCase() === location.pathname?.toLowerCase())

//     // If no match found, use fallback
//     if (activeIndex === -1) {
//       activeIndex = Math.min(fallbackTabIndex, tabs.length - 1)
//     }

//     // Ensure the tab at this index is not disabled
//     while (activeIndex < tabs.length && tabs[activeIndex]?.disabled) {
//       activeIndex++
//     }

//     // If all tabs after fallback are disabled, look backwards
//     if (activeIndex >= tabs.length) {
//       activeIndex = Math.max(0, fallbackTabIndex - 1)
//       while (activeIndex >= 0 && tabs[activeIndex]?.disabled) {
//         activeIndex--
//       }
//     }

//     setDefaultTabIndex(activeIndex >= 0 ? activeIndex : 0)
//     setIsInitialized(true)

//     // Trigger callback if provided
//     if (onTabChange && activeIndex >= 0 && tabs[activeIndex]) {
//       onTabChange(tabs[activeIndex], activeIndex)
//     }
//   }, [tabs, tabs, fallbackTabIndex, onTabChange, location.pathname, subName, name, id, subId, projectPermission])
//   // }, [tabs, tabs, getCurrentSlug, fallbackTabIndex, onTabChange])

//   // Handle tab navigation
//   const handleTabClick = useCallback(
//     (url: string) => {
//       if (preserveQueryParams && location.search) {
//         console.log('iffffff')

//         navigate(`${url}${location.search}`)
//       } else {
//         console.log('elseee', url)
//         navigate(url)
//       }
//     },
//     [navigate, preserveQueryParams, location.search, name, id, projectPermission],
//   )

//   // Transform tabs for CustomTabs component
//   const transformedTabs = useMemo(
//     () =>
//       tabs.map((tab: TabConfig) => ({
//         label: tab.label,
//         component: children,
//         value: generateURL(tab),
//         disabled: tab.disabled,
//         badge: tab.badge,
//         icon: tab.icon,
//       })),
//     [tabs, children, generateURL, name, id, projectPermission, location?.pathname],
//   )

//   // Show loading state
//   if (!isInitialized || loading || defaultTabIndex === null) {
//     return (
//       <DashboardLayout breadcrumbData={breadcrumbData}>
//         <div className={`grid grid-cols-12 h-full ${className}`}>
//           <div className="col-span-12">
//             <FullScreenLoader loading={true} />
//           </div>
//         </div>
//       </DashboardLayout>
//     )
//   }

//   // Show error state if no visible tabs
//   if (tabs.length === 0) {
//     return (
//       <DashboardLayout breadcrumbData={breadcrumbData}>
//         <div className={`grid grid-cols-12 h-full ${className}`}>
//           <div className="col-span-12 flex items-center justify-center">
//             <div className="text-center">
//               <h3 className="text-lg font-medium text-gray-900 mb-2">No tabs available</h3>
//               <p className="text-gray-500">Please check your tab configuration.</p>
//             </div>
//           </div>
//         </div>
//       </DashboardLayout>
//     )
//   }

//   return (
//     <DashboardLayout breadcrumbData={breadcrumbData} page={page} parentPage={parentPage}>
//       <div className={`grid grid-cols-12 h-full ${className}`}>
//         <div className="col-span-12">
//           {console.log('URLS', transformedTabs)}
//           <CustomTabs handleClick={handleTabClick} tabs={transformedTabs} defaultTabIndex={defaultTabIndex} />
//         </div>
//       </div>
//     </DashboardLayout>
//   )
// }

// export default TabsLayout
